import React, {FC, useEffect, useMemo, useState} from 'react'
import {Link, useHistory, useParams} from 'react-router-dom'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {formatDateTime} from '../shared/DocumentApi'
import {getJobById, getJobDashboard, loadJobs} from './Api'
import {HR_JOB_PATH, IJobDashboardModel, IJobModel, jobDashboardPath, publicJobUrl} from './JobModel'
import {ShareJobModal} from './ShareJobModal'

const RANGE_OPTIONS = [
  {days: 7, label: '7 gün'},
  {days: 30, label: '30 gün'},
  {days: 90, label: '90 gün'},
  {days: 0, label: 'Tümü'},
]

const RESULT_COLORS = {positive: '#50cd89', negative: '#f1416c', rejected: '#ffc700'}

const StatCard: FC<{label: string; value: number; icon: string; color: string; hint?: string}> = ({
  label,
  value,
  icon,
  color,
  hint,
}) => (
  <div className='col-sm-6 col-xl-3'>
    <div className='card h-100'>
      <div className='card-body d-flex align-items-center gap-5'>
        <div className='symbol symbol-55px symbol-circle'>
          <span className={`symbol-label bg-light-${color}`}>
            <i className={`bi ${icon} fs-2x text-${color}`}></i>
          </span>
        </div>
        <div className='d-flex flex-column'>
          <span className='fs-2hx fw-bolder text-gray-900 lh-1'>{value}</span>
          <span className='text-muted fw-semibold mt-2'>{label}</span>
          {hint && <span className='text-gray-500 fs-8'>{hint}</span>}
        </div>
      </div>
    </div>
  </div>
)

const formatDay = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('tr-TR', {day: '2-digit', month: 'short'})

/** İlan dashboard'u: başvuru / sonuç sayıları, günlük başvuru grafiği ve sonuç dağılımı */
const JobDashboardPage: FC = () => {
  const {id} = useParams<{id: string}>()
  const history = useHistory()
  const [dashboard, setDashboard] = useState<IJobDashboardModel | null>(null)
  const [job, setJob] = useState<IJobModel | null>(null)
  const [jobs, setJobs] = useState<IJobModel[]>([])
  const [rangeDays, setRangeDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState(false)

  useEffect(() => {
    setLoading(true)
    Promise.all([getJobDashboard(id), getJobById(id)])
      .then(([dashboardResponse, jobResponse]) => {
        setDashboard(dashboardResponse.data.payload)
        setJob(jobResponse.data.payload)
      })
      .catch((error) => showErrorToast(apiErrorMessage(error, 'İlan dashboard\'u yüklenemedi')))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    loadJobs(0, 1000)
      .then(({data}) => setJobs(data.payload.content))
      .catch(() => setJobs([]))
  }, [])

  const dailySeries = useMemo(() => {
    const days = dashboard?.dailyApplications ?? []
    return rangeDays > 0 ? days.slice(-rangeDays) : days
  }, [dashboard, rangeDays])

  const dailyOptions: ApexOptions = useMemo(
    () => ({
      chart: {type: 'area', toolbar: {show: false}, zoom: {enabled: false}, fontFamily: 'inherit'},
      dataLabels: {enabled: false},
      stroke: {curve: 'smooth', width: 3},
      colors: ['#009ef7'],
      fill: {type: 'gradient', gradient: {opacityFrom: 0.35, opacityTo: 0.05}},
      xaxis: {
        categories: dailySeries.map((day) => formatDay(day.date)),
        tickAmount: Math.min(dailySeries.length, 12),
        labels: {rotate: 0, style: {colors: '#a1a5b7'}},
      },
      yaxis: {min: 0, forceNiceScale: true, labels: {formatter: (value) => `${Math.round(value)}`}},
      grid: {borderColor: '#eff2f5', strokeDashArray: 4},
      tooltip: {y: {formatter: (value) => `${value} başvuru`}},
    }),
    [dailySeries]
  )

  const resultTotal = dashboard ? dashboard.positiveCount + dashboard.negativeCount + dashboard.rejectedCount : 0

  const resultOptions: ApexOptions = useMemo(
    () => ({
      chart: {type: 'donut', fontFamily: 'inherit'},
      labels: ['Olumlu', 'Olumsuz', 'Reddedildi'],
      colors: [RESULT_COLORS.positive, RESULT_COLORS.negative, RESULT_COLORS.rejected],
      legend: {position: 'bottom'},
      dataLabels: {enabled: true},
      plotOptions: {
        pie: {donut: {size: '62%', labels: {show: true, total: {show: true, label: 'Toplam'}}}},
      },
      tooltip: {y: {formatter: (value) => `${value} aday`}},
    }),
    []
  )

  return (
    <>
      <PageTitle breadcrumbs={[{title: 'İş İlanları', path: HR_JOB_PATH, isActive: false}]}>
        İlan Dashboard'u
      </PageTitle>

      <div className='card mb-6'>
        <div className='card-body d-flex flex-wrap align-items-center justify-content-between gap-4 py-6'>
          <div className='d-flex flex-column'>
            <div className='d-flex align-items-center gap-3'>
              <span className='fs-2 fw-bolder text-gray-900'>{dashboard?.title ?? '...'}</span>
              {dashboard &&
                (dashboard.status === 'ACTIVE' ? (
                  <span className='badge badge-light-success'>Yayında</span>
                ) : (
                  <span className='badge badge-light-secondary'>Pasif</span>
                ))}
            </div>
            {dashboard?.createdDate && (
              <span className='text-muted fs-7 mt-1'>Yayın tarihi: {formatDateTime(dashboard.createdDate)}</span>
            )}
          </div>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            <select
              className='form-select form-select-solid form-select-sm w-auto'
              style={{minWidth: 220}}
              value={id}
              onChange={(event) => history.push(jobDashboardPath(event.target.value))}
            >
              {jobs.length === 0 && <option value={id}>{dashboard?.title}</option>}
              {jobs.map((option) => (
                <option key={option.id || ''} value={option.id || ''}>
                  {option.title}
                </option>
              ))}
            </select>
            {dashboard?.status === 'ACTIVE' && (
              <a
                href={publicJobUrl(id)}
                target='_blank'
                rel='noopener noreferrer'
                className='btn btn-sm btn-light-info'
              >
                <i className='bi bi-box-arrow-up-right'></i>
                İlanı Aç
              </a>
            )}
            <button type='button' className='btn btn-sm btn-primary' disabled={!job} onClick={() => setSharing(true)}>
              <i className='bi bi-share'></i>
              İlanı Paylaş
            </button>
            <Link to={HR_JOB_PATH} className='btn btn-sm btn-light'>
              İlanlara Dön
            </Link>
          </div>
        </div>
      </div>

      {loading || !dashboard ? (
        <div className='d-flex justify-content-center py-20'>
          <span className='spinner-border text-primary' />
        </div>
      ) : (
        <>
          <div className='row g-6 mb-6'>
            <StatCard
              label='Başvuru'
              value={dashboard.applicationCount}
              icon='bi-inbox'
              color='primary'
              hint={`${dashboard.inProgressCount} süreçte · ${dashboard.notStartedCount} başlatılmadı`}
            />
            <StatCard
              label='Olumlu'
              value={dashboard.positiveCount}
              icon='bi-hand-thumbs-up'
              color='success'
              hint='Süreci olumlu tamamlanan'
            />
            <StatCard
              label='Olumsuz'
              value={dashboard.negativeCount}
              icon='bi-hand-thumbs-down'
              color='danger'
              hint='Süreci olumsuz tamamlanan'
            />
            <StatCard
              label='Reddedildi'
              value={dashboard.rejectedCount}
              icon='bi-x-octagon'
              color='warning'
              hint='Soru koşulunu sağlamayan'
            />
          </div>

          <div className='row g-6'>
            <div className='col-xl-8'>
              <div className='card h-100'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-4 mb-1'>Günlük Başvuru</span>
                    <span className='text-muted fw-semibold fs-7'>
                      Seçilen dönemde {dailySeries.reduce((total, day) => total + day.count, 0)} başvuru
                    </span>
                  </h3>
                  <div className='card-toolbar'>
                    <div className='btn-group btn-group-sm'>
                      {RANGE_OPTIONS.map((option) => (
                        <button
                          key={option.days}
                          type='button'
                          className={`btn btn-sm ${rangeDays === option.days ? 'btn-primary' : 'btn-light'}`}
                          onClick={() => setRangeDays(option.days)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className='card-body pt-0'>
                  <Chart
                    type='area'
                    height={320}
                    options={dailyOptions}
                    series={[{name: 'Başvuru', data: dailySeries.map((day) => day.count)}]}
                  />
                </div>
              </div>
            </div>

            <div className='col-xl-4'>
              <div className='card h-100'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder fs-4 mb-1'>Sonuç Dağılımı</span>
                    <span className='text-muted fw-semibold fs-7'>Olumlu, olumsuz ve reddedilen adaylar</span>
                  </h3>
                </div>
                <div className='card-body pt-0 d-flex align-items-center justify-content-center'>
                  {resultTotal === 0 ? (
                    <div className='text-center text-muted py-10'>
                      <i className='bi bi-pie-chart fs-3x d-block mb-3 text-gray-300'></i>
                      Henüz sonuçlanan aday yok
                    </div>
                  ) : (
                    <Chart
                      type='donut'
                      height={320}
                      width='100%'
                      options={resultOptions}
                      series={[dashboard.positiveCount, dashboard.negativeCount, dashboard.rejectedCount]}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <ShareJobModal
        job={sharing && job && job.id ? {...job, id: job.id} : null}
        onClose={() => setSharing(false)}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {JobDashboardPage}
